import tokenConfig from "@/config/tokens.json";
import axios from "axios";
import { getOracleRegistryState } from "redstone-sdk";
import Vue from "vue";

const GITHUB_BASE_URL = "https://raw.githubusercontent.com/redstone-finance/redstone-oracles-monorepo/refs/heads/main/packages/node-remote-config/dev/manifests/data-services"
const DEFAULT_PROVIDER = "coingecko";

export default {
  namespaced: true,
  
  state: {
    manifestsLoadComplete: false,
    manifests: {},
    symbolDetails: null,
    cachedManifests: null,
    providers: null,
  },

  getters: {
    isLoadComplete: state => state.manifestsLoadComplete,
    getAllManifests: state => state.manifests,
    getSymbolDetails: state => state.symbolDetails,
    denominationMap: state => {
      const denominationMap = {};
      if (state.symbolDetails) {
        Object.keys(state.symbolDetails)?.forEach((symbol) => {
          denominationMap[symbol] = {
            rateFeed: state.symbolDetails[symbol].config?.rateFeed,
            denomination: state.symbolDetails[symbol].config?.denomination,
          };
        });
      }
      return denominationMap;
    },
    isCurrencyToken: () => (details) => !details.tags?.includes("lens"),
    getCurrency: () => (details) => {
      if (details.name?.includes("/")) {
        const [, currency] = details.name?.split("/");
        return currency.replaceAll(" ", "-");
      }
      if (details.symbol?.includes("/")) {
        const [, currency] = details.symbol?.split("/");
        return currency.replaceAll(" ", "-");
      }
      return "USD";
    }
  },

  mutations: {
    setProviders(state, providers) {
      state.providers = { ...providers };
    },
    SET_MANIFESTS(state, manifests) {
      state.manifests = { ...state.manifests, ...manifests };
    },
    SET_LOAD_COMPLETE(state, value) {
      state.manifestsLoadComplete = value;
    },
    SET_SYMBOL_DETAILS(state, details) {
      state.symbolDetails = details;
    },
    SET_CACHED_MANIFESTS(state, manifests) {
      state.cachedManifests = manifests;
    }
  },

  actions: {
    updateProvider({ commit, state }, { id, key, value }) {
      let providers = state.providers;
      Vue.set(providers[id], key, value);
      commit("setProviders", providers);
    },
    async fetchProviders({state, commit, dispatch }) {
      const contractState = await getOracleRegistryState();
      const providers = contractState.dataServices;
      delete providers["redstone-custom-urls-demo"];
      commit("setProviders", providers);
      for (const [providerId, provider] of Object.entries(providers)) {
        Vue.set(providers, providerId, provider);
        commit("setProviders", providers);
        const manifestName = providerId.split("-")[1];
        const manifest = state.manifests[manifestName]
        dispatch("updateProvider", {
          id: providerId,
          key: "currentManifest",
          value: manifest,
        });
        if (manifest?.tokens) {
          const assetsCount = Object.keys(manifest.tokens).length;
          dispatch("updateProvider", {
            id: providerId,
            key: "assetsCount",
            value: assetsCount,
          });
        } else {
          dispatch("updateProvider", {
            id: providerId,
            key: "assetsCount",
            value: 0,
          });
        }
        const nodes = contractState.nodes;
        const filteredNodes = Object.values(nodes).filter(
          (node) => node.dataServiceId === providerId
        );
        dispatch("updateProvider", {
          id: providerId,
          key: "nodes",
          value: filteredNodes,
        });
      }
    },
    async fetchManifest({ commit }, manifestName) {
      try {
        const { data } = await axios.get(`${GITHUB_BASE_URL}/${manifestName}.json`);
        return data;
      } catch (error) {
        console.error(`Fetching manifest failed, ${manifestName} couldn't be loaded`, { error });
        return {};
      }
    },

    async initializeManifests({ state, commit, dispatch }) {
      if (state.cachedManifests) {
        return state.cachedManifests;
      }

      const [
        mainManifest,
        primaryManifest,
        arbitrumManifest,
        avalancheManifest
      ] = await Promise.all([
        this.dispatch('manifests/fetchManifest', 'main'),
        this.dispatch('manifests/fetchManifest', 'primary'),
        this.dispatch('manifests/fetchManifest', 'arbitrum'),
        this.dispatch('manifests/fetchManifest', 'avalanche')
      ]);

      dispatch('setLoadComplete');

      const primaryManifestCombined = {
        ...primaryManifest,
        tokens: {
          ...primaryManifest.tokens
        },
      };

      const manifests = {
        "redstone-primary-demo": mainManifest,
        "redstone-primary-prod": primaryManifestCombined,
        "redstone-avalanche-prod": avalancheManifest,
        "redstone-arbitrum-prod": arbitrumManifest,
      };

      commit('SET_CACHED_MANIFESTS', manifests);
      commit('SET_MANIFESTS', manifests);
      return manifests;
    },

    async getAllSymbolDetails({ state, commit, dispatch }) {
      if (state.symbolDetails) {
        return state.symbolDetails;
      }

      const tokenDetails = new Map();
      const manifestsObject = await dispatch('initializeManifests');

      // Initialize default provider tokens
      Object.entries(tokenConfig).forEach(([symbol, config]) => {
        tokenDetails.set(symbol, {
          ...config,
          providers: [DEFAULT_PROVIDER],
          config: {}
        });
      });

      // Single pass through manifests
      Object.entries(manifestsObject).forEach(([provider, manifest]) => {
        const tokens = manifest.tokens || {};
        Object.entries(tokens).forEach(([symbol, config]) => {
          const existing = tokenDetails.get(symbol) || { 
            providers: ['redstone-primary-prod'], 
            config: {} 
          };

          if (provider !== DEFAULT_PROVIDER && config.source?.length && provider !== 'redstone-arbitrum-prod') {
            existing.providers = ['redstone-primary-prod'];
          }

          existing.config = { ...existing.config, ...config };
          tokenDetails.set(symbol, existing);
        });
      });

      // Convert to object and sort
      const sortedDetails = [...tokenDetails.entries()]
        .sort(([, a], [, b]) => 
          (a.providers[0] === DEFAULT_PROVIDER) - (b.providers[0] === DEFAULT_PROVIDER)
        )
        .reduce((acc, [symbol, config]) => {
          acc[symbol] = config;
          return acc;
        }, {});

      commit('SET_SYMBOL_DETAILS', sortedDetails);
      return sortedDetails;
    },

    async getDetailsForSymbol({ dispatch }, symbol) {
      const details = await dispatch('getAllSymbolDetails');
      return details[symbol];
    },

    async getOrderedProviders({ dispatch }) {
      const manifests = await dispatch('initializeManifests');
      return Object.keys(manifests);
    },

    setLoadComplete({ commit }) {
      commit('SET_LOAD_COMPLETE', true);
    }
  }
};
